import { css, cx } from '@emotion/css';
import { useTheme } from '@tcc-online/react-theme-provider';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, NavLink } from 'react-router-dom';
import { Translate, useTranslate } from 'react-translate.ts';

import { AuthActions } from '../../../actions';
import {
    ADMIN_COMPANIES_PATH,
    ADMIN_COMPANY_PATH,
    ADMIN_DASHBOARD_PATH,
    ADMIN_PATH,
    OUTPUT_REPORT_PATH,
    PARTICIPANT_PATH,
    PASSWORD_PATH,
    PROFILE_PATH,
} from '../../../constants/route.constants';
import getFlagIconClassname from '../../../helpers/getFlagIconClassname';
import AuthSelectors from '../../../selectors/AuthSelectors';
import { LanguageService } from '../../../services';
import ContactButton from '../ContactButton';
import ContactModal from '../ContactModal/ContactModal';
import useContactModal from '../useContactModal';
import styles from './NavMenuMobile.module.scss';
import ListIcon from 'react-bootstrap-icons/dist/icons/list';
import XIcon from 'react-bootstrap-icons/dist/icons/x';
import { TRANSLATIONS_ROOT_KEY } from '../../../constants/translation.constants';
import LanguageSlice from '../../../common/slices/LanguageSlice';
import { AppDispatch } from '../../../reducers/store';
import useLanguages from '../../../hooks/useLanguages';
import { UserService } from '../../../services/common/UserService';
import navBarStyles from '../Navbar.module.scss';

type NavMenuMobileProps = {
    isUserLogged: boolean;
    isAdmin: boolean;
    companyId: number | null;
    companyName: string | null;
};

export default function NavMenuMobile(props: NavMenuMobileProps) {
    const { isUserLogged, isAdmin, companyId, companyName } = props;

    const [expanded, setExpanded] = useState(false);
    const [theme] = useTheme();
    const contactModalState = useContactModal();
    const rootElement = useMemo(
        () => document.body.querySelector('#root') as HTMLElement,
        [],
    );

    useEffect(() => {
        if (expanded) {
            rootElement.style.position = 'fixed';
        } else {
            rootElement.style.position = '';
        }

        return enableScroll;

        function enableScroll() {
            rootElement.style.position = '';
        }
    }, [expanded]);

    return (
        <div className={styles.nav}>
            <MenuButton expanded={expanded} onClick={setExpanded} />
            {expanded ? (
                <ul
                    className={css({
                        background: theme.surfaceColor,
                    })}
                >
                    <PrivateItems
                        isUserLogged={isUserLogged}
                        onLogout={closeMenu}
                        onSelect={closeMenu}
                        isAdmin={isAdmin}
                        companyId={companyId}
                        companyName={companyName}
                    />
                    <Item onClick={contactModalState.toggle}>
                        <ContactButton />
                    </Item>
                    <ContactModal
                        isLogged={isUserLogged}
                        isVisible={contactModalState.isVisible}
                        onClose={contactModalState.close}
                    />
                    <Language onSelect={closeMenu} />
                </ul>
            ) : null}
        </div>
    );

    function closeMenu() {
        setExpanded(false);
    }
}

type MenuButtonProps = {
    expanded: boolean;
    onClick: (expanded: boolean) => void;
};

function MenuButton(props: MenuButtonProps) {
    const { expanded, onClick } = props;
    const [theme] = useTheme();

    return (
        <div
            className={cx(
                styles.button,
                css({ '&:hover': { color: theme.primaryColor } }),
            )}
            onClick={() => onClick(!expanded)}
            data-test="mobile-nav-menu-button"
        >
            {!expanded ? <ListIcon /> : <XIcon />}
        </div>
    );
}

function PrivateItems(
    props: NavMenuMobileProps & { onLogout: () => void; onSelect: () => void },
) {
    const {
        isUserLogged,
        isAdmin,
        companyId,
        onLogout,
        onSelect,
        companyName,
    } = props;

    const translate = useTranslate(TRANSLATIONS_ROOT_KEY + '.nav');
    const email = AuthSelectors.useGetEmail();

    const isAnonymous = useSelector(AuthSelectors.isAnonymous);

    if (!isUserLogged) {
        return null;
    }

    return (
        <>
            <Item onClick={onSelect}>
                <NavLink to={PARTICIPANT_PATH} className={navBarStyles.item}>
                    {translate('todo')}
                </NavLink>
            </Item>
            {!isAnonymous && (
                <Item onClick={onSelect}>
                    <NavLink
                        to={OUTPUT_REPORT_PATH}
                        className={navBarStyles.item}
                    >
                        {translate('results')}
                    </NavLink>
                </Item>
            )}
            {isAdmin && (
                <Item onClick={onSelect}>
                    <NavLink
                        to={`${ADMIN_PATH}/${ADMIN_DASHBOARD_PATH}`}
                        className={navBarStyles.item}
                    >
                        <Translate>fe.nav.admin</Translate>
                    </NavLink>
                </Item>
            )}
            <Item
                data-test="mobile-nav-menu-profile"
                expandable
                subItems={
                    <>
                        {isAnonymous !== true && (
                            <Item onClick={onSelect}>
                                <NavLink to={PROFILE_PATH}>
                                    {translate('user.profile')}
                                </NavLink>
                            </Item>
                        )}
                        {isAdmin && companyId && (
                            <Item onClick={onSelect}>
                                <NavLink
                                    to={generatePath(
                                        `${ADMIN_PATH}/${ADMIN_COMPANIES_PATH}/${ADMIN_COMPANY_PATH}`,
                                        {
                                            companyId: companyId.toString(),
                                        },
                                    )}
                                >
                                    {companyName || translate('spolecnost')}
                                    {/* TODO: translate */}
                                </NavLink>
                            </Item>
                        )}
                        {UserService.isAnonymous() !== true && (
                            <Item onClick={onSelect}>
                                <NavLink to={PASSWORD_PATH}>
                                    {translate('user.change-password')}
                                </NavLink>
                            </Item>
                        )}
                        <SignOut onLogout={onLogout} isLogged={isUserLogged} />
                    </>
                }
            >
                {email || translate('user.profile')}
            </Item>
        </>
    );
}

type ItemProps = {
    expandable?: boolean;
    subItems?: ReactNode;
    onClick?: () => void;
    subListClassName?: string;
    children?: ReactNode;
};

function Item(props: ItemProps) {
    const {
        expandable,
        subItems,
        onClick,
        subListClassName,
        children,
        ...rest
    } = props;
    const [expanded, setExpanded] = useState(false);
    const [theme] = useTheme();

    return (
        <>
            <li
                className={css({
                    '&:hover': {
                        background: theme.primaryColor,
                        color: theme.textLightColor,
                        '& a': {
                            color: theme.textLightColor,
                        },
                    },
                })}
                onClick={() => {
                    if (onClick) {
                        onClick();
                        return;
                    }

                    if (!expandable) {
                        return;
                    }

                    setExpanded(!expanded);
                }}
                {...rest}
            >
                {children}
            </li>
            {expanded ? (
                <ul
                    className={cx(
                        styles.sublist,
                        subListClassName,
                        css({
                            '& li': {
                                background: `${theme.backgroundColor} !important`,
                            },
                        }),
                    )}
                >
                    {subItems}
                </ul>
            ) : null}
        </>
    );
}

function SignOut(props: { isLogged: boolean; onLogout: () => void }) {
    const dispatch = useDispatch();

    if (!props.isLogged) {
        return null;
    }

    return (
        <Item onClick={logout} data-test="mobile-nav-menu-signout">
            <Translate>fe.nav.user.logout</Translate>
        </Item>
    );

    function logout() {
        dispatch(AuthActions.logout());
        props.onLogout();
    }
}

function Language(props: { onSelect: () => void }) {
    const currentLocale = useSelector(LanguageSlice.state.getLocale);
    const dispatch = useDispatch<AppDispatch>();
    const allowedLanguages = useLanguages();

    return (
        <Item
            expandable
            subItems={renderLocales(allowedLanguages)}
            subListClassName={styles.language}
        >
            <div className={getFlagIconClassname(currentLocale)}></div>
        </Item>
    );

    function renderLocales(locales: string[]) {
        return locales.map((locale, i) => {
            const language = LanguageService.getLanguageNativeName(locale);

            if (
                currentLocale === locale ||
                !allowedLanguages.includes(locale.toLowerCase())
            ) {
                return null;
            }

            return (
                <li key={i} onClick={changeLocale(locale)}>
                    <div className={getFlagIconClassname(locale)} />{' '}
                    <span>{language}</span>
                </li>
            );
        });

        function changeLocale(locale) {
            return async () => {
                dispatch(LanguageSlice.actions.setLoading(true));
                await dispatch(LanguageSlice.actions.changeLocale(locale));
                props.onSelect();
            };
        }
    }
}
